<template>
    <div>
        <Action class="z-10000 focus:outline-hidden" :items="items">
            <template #activator="{ onClick }">
                <div
                    class="flex cursor-pointer items-center gap-3"
                    @click="onClick"
                >
                    <div
                        class="mt-[6px] h-[30px] w-[30px] cursor-pointer rounded-full ring-[1px] ring-gray-300"
                    >
                        <img
                            :src="profilePhoto"
                            class="h-[30px] w-[30px] rounded-full object-cover"
                            alt="Profile photo menu element"
                        />
                    </div>

                    <div class="mt-[6px] text-sm text-white">
                        {{ userName }}
                    </div>
                </div>
            </template>
        </Action>
        <CreateTeamModal
            v-if="createTeamModal"
            @close="createTeamModal = false"
            @close-and-switch-to-team="handleCloseAndSwitchToTeam"
        />
    </div>
</template>

<script lang="ts" setup>
import { ref, computed } from "vue";
import { router } from "@inertiajs/vue3";
import { usePage } from "@inertiajs/vue3";
import route from "ziggy-js";
import { Cog8ToothIcon, PlusCircleIcon } from "@heroicons/vue/24/outline";
import type IActionNode from "@/types/Atomic/IActions";
import type IUser from "@/types/Members/IUser";
import { useTranslations } from "@/composables/useTranslations";
import type { IRhizomPageProps } from "@/types/RhizomPageProps/RhizomPageProps.ts";

const { t } = useTranslations();
const page = usePage<IRhizomPageProps>();

const { getCurrentTeamMembersCount } = useTeamsAndMembers();

let createTeamModal = ref(false);

const { isViewer, isEditor, isAdmin, isOwner } = usePermissions();

const handleCloseAndSwitchToTeam = () => {
    createTeamModal.value = false;
    handleFetchBannersAndMembers();
};

const handleFetchBannersAndMembers = () => {
    getCurrentTeamMembersCount();
};

const items: IActionNode[] = [
    {
        name: t("vue.my_profile"),
        onClick() {
            router.visit(route("settings.profile-settings.index"), {
                onSuccess: () => {
                    router.reload();
                },
            });
        },
    },
    {
        name: t("vue.billing"),
        onClick() {
            router.visit(route("billing.subscriptions.index"), {
                onSuccess: () => {
                    router.reload();
                },
            });
        },
    },
    {
        name: t("vue.integrations"),
        onClick() {
            router.visit(route("integrations.available.index"), {
                onSuccess: () => {
                    router.reload();
                },
            });
        },
    },
    {
        name: t("vue.members"),
        onClick() {
            router.visit(route("members.index"), {
                onSuccess: () => {
                    router.reload();
                },
            });
        },
    },
    {
        name: t("vue.logout"),
        onClick() {
            router.post(route("logout"));
        },
    },
];

if (page.props.hasFreePlan) {
    items.splice(3, 1);
}

if (isOwner.value || isAdmin.value) {
    items.splice(1, 0, {
        name: t("vue.account_settings"),
        onClick() {
            router.visit(route("settings.team-settings.index"), {
                onSuccess: () => {
                    router.reload();
                },
            });
        },
    });
}

const profilePhoto = computed(() => {
    const user = usePage().props.user as IUser;
    return user.profile_photo_url;
});

const userName = computed(() => {
    const user = usePage().props.user as IUser;
    return user.name;
});
</script>
